import { throwErrorWithId } from '@pypestream/utils';

import {
  getPypeKeysForPype,
  type ReportingApiCredentialRow,
} from '../../utils';
import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';

export interface ChannelApiCredentials {
  pypeId: string;
  env: 'live' | 'sandbox';
  rows: ReportingApiCredentialRow[];
}

/**
 * PSTUDIO-4253 — the Reporting API credentials of ONE channel.
 *
 * Channel Settings used to dispatch `loadApiCredentials`, which is written for
 * the project-wide Reporting API Credentials screen: it fetches every pype in
 * the project (`getCustomerPypes` with no `botId`) and then one keys request per
 * pype per env — `1 + 2P` requests, roughly 85 on a 40-solution project — while
 * the form displays only the keys of the pype the open channel belongs to, in a
 * block that lives on a DIFFERENT tab from the style sheet the user came to
 * edit.
 *
 * That mattered far beyond the wasted traffic: channel mutations are handled
 * only in the machine's `idle` state, so Save waits for this chain to finish
 * (`waitForProStudioIdle`, 30s cap). With several tabs open — the reporter's
 * actual workflow, one solution per tab — the same fan-out runs once per tab
 * against one backend, and the wait grew until "Save changes" looked like it did
 * nothing at all.
 *
 * So this asks for exactly what the page shows: ONE request. It writes its own
 * context slice rather than `ctx.apiCredentials`, which the Reporting API
 * Credentials screen gates on being non-null and renders in full — narrowing the
 * shared slice would have made that screen list a single pype and believe it was
 * done.
 *
 * `botPypes` is already loaded and already solution-scoped, so resolving the
 * pype costs nothing. A pype that is not in it is an error, not an empty list:
 * "we could not look up this channel's keys" and "this channel has no keys" are
 * different claims, and the block has a distinct rendering for each.
 */
export const loadChannelApiCredentials = async (
  ctx: ProStudioContext,
  event: ProStudioEvents
): Promise<{ channelApiCredentials: ChannelApiCredentials } | void> => {
  if (event.type !== 'loadChannelApiCredentials') {
    return;
  }

  const { pypeId, env } = event.payload;

  const projectId = ctx.project?.id;
  const customerId = ctx.project?.projectProStudioSetting?.customerId;

  if (!projectId) {
    throwErrorWithId('Project ID is required');
    return;
  }

  if (!customerId) {
    throwErrorWithId('Customer ID is required');
    return;
  }

  const pype = (ctx.botPypes ?? []).find(
    (candidate) => candidate.id === pypeId && candidate.env === env
  );

  if (!pype) {
    throwErrorWithId(`No pype ${pypeId} in ${env} for this solution`);
    return;
  }

  const rows = await getPypeKeysForPype({
    pype,
    projectId,
    customerId,
    env,
  });

  return { channelApiCredentials: { pypeId, env, rows } };
};
